import { useState } from "react";
import { toast } from "sonner";
import { CalendarOff, Trash2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errMsg, useApi } from "@/lib/api";

const todayWib = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
const fmtDay = (d) => new Date(`${d}T00:00:00+07:00`).toLocaleDateString("id-ID", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Jakarta" });

export const HolidayManager = ({ canEdit }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const { data, reload } = useApi(open ? "/chat-admin/holidays" : null);
  const list = (data || []).filter((h) => showPast || h.date >= todayWib());

  const add = async () => {
    try {
      await api.post("/chat-admin/holidays", { date, name });
      toast.success("Tanggal libur disimpan");
      setDate("");
      setName("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (d) => {
    if (confirm !== d) {
      setConfirm(d);
      return toast.info("Tekan ikon hapus sekali lagi untuk menghapus");
    }
    setConfirm(null);
    try {
      await api.delete(`/chat-admin/holidays/${d}`);
      toast.success("Tanggal libur dihapus");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="holiday-open" className="flex items-center gap-1.5 text-sm font-semibold border border-slate-200 bg-white hover:bg-slate-50 px-3 py-2 rounded-xl">
        <CalendarOff className="h-4 w-4 text-rose-600" /> Hari Libur
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="holiday-manager">
          <DialogHeader>
            <DialogTitle>Hari Libur CU</DialogTitle>
            <DialogDescription>Pada tanggal ini chat di luar jam layanan dan anggota mendapat balasan otomatis. Sudah terisi libur nasional & cuti bersama 2026–2027.</DialogDescription>
          </DialogHeader>
          {canEdit && (
            <div className="flex flex-wrap gap-2 bg-slate-50 rounded-xl p-3">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-[160px]" data-testid="holiday-date-input" />
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Keterangan, contoh: RAT CU" className="flex-1 min-w-[160px]" data-testid="holiday-name-input" />
              <button onClick={add} disabled={!date || name.trim().length < 2} data-testid="holiday-add"
                className="flex items-center gap-1 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg"><Plus className="h-4 w-4" />Tambah</button>
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} data-testid="holiday-show-past" /> Tampilkan tanggal yang sudah lewat
          </label>
          <div className="space-y-1.5" data-testid="holiday-list">
            {list.length === 0 && <p className="text-sm text-slate-500">Tidak ada tanggal libur.</p>}
            {list.map((h) => (
              <div key={h.date} className="flex items-center gap-3 border border-slate-100 rounded-xl px-3 py-2" data-testid={`holiday-row-${h.date}`}>
                <span className="text-xs font-bold text-rose-700 w-[120px] shrink-0">{fmtDay(h.date)}</span>
                <span className="text-sm text-slate-800 flex-1">{h.name}</span>
                {canEdit && (
                  <button onClick={() => remove(h.date)} data-testid={`holiday-delete-${h.date}`} aria-label="Hapus"
                    className={`p-1.5 hover:text-red-600 ${confirm === h.date ? "text-red-600 bg-red-50 rounded-lg" : "text-slate-400"}`}><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
